import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Search, User, Menu, X, Home, Package, Heart, ChevronRight } from "lucide-react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

export default function StoreLayout({ children }) {
  const { user } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState({ store_name: "CPM Store", logo_url: "", accent_color: "#C5A028" });
  const [collections, setCollections] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    http.get("/store/settings").then(r => setSettings(r.data)).catch(() => {});
    http.get("/store/collections").then(r => setCollections(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/store/catalog?q=${encodeURIComponent(searchQ.trim())}`);
  };

  const banner = settings.announcement_banner;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}>
      {/* Announcement banner */}
      {banner && (
        <div className="text-center py-2 px-4 text-sm font-semibold text-white" style={{ background: "#0F1E38" }}>
          {banner}
        </div>
      )}

      {/* Main header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          {/* Logo */}
          <Link to="/store" className="flex items-center gap-2 shrink-0 mr-2">
            {settings.logo_url
              ? <img src={settings.logo_url} alt={settings.store_name} className="h-8 w-auto" />
              : <span className="font-bold text-lg" style={{ color: "#0F1E38", fontFamily: "Cormorant Garamond, serif" }}>{settings.store_name || "CPM Store"}</span>
            }
          </Link>

          {/* Collections nav — desktop */}
          <nav className="hidden md:flex items-center gap-5 flex-1">
            <Link to="/store/catalog" className={`text-sm font-medium transition-colors hover:text-yellow-600 ${location.pathname === "/store/catalog" ? "text-yellow-600" : "text-gray-600"}`}>All Products</Link>
            {collections.map(c => (
              <Link key={c.id} to={`/store/catalog?collection=${c.slug}`} className="text-sm font-medium text-gray-600 hover:text-yellow-600 transition-colors whitespace-nowrap">
                {c.name}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <form onSubmit={onSearch} className="flex-1 max-w-xs hidden md:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2">
            <Search size={15} className="text-gray-400 shrink-0" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search products…"
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3 ml-auto md:ml-0">
            <Link to="/store/cart" className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
              <ShoppingCart size={20} className="text-gray-700" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full text-xs font-bold text-white grid place-items-center" style={{ background: "#C5A028" }}>{count}</span>
              )}
            </Link>
            {user
              ? <Link to="/store/orders" className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-yellow-600 transition-colors"><User size={16} /> Orders</Link>
              : <Link to="/auth" className="hidden md:flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold text-white" style={{ background: "#0F1E38" }}>Sign in</Link>
            }
            <button onClick={() => setMobileMenuOpen(o => !o)} className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-white pt-16 overflow-y-auto" onClick={() => setMobileMenuOpen(false)}>
          <div className="p-4 space-y-1">
            <form onSubmit={(e) => { onSearch(e); setMobileMenuOpen(false); }} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search products…" className="flex-1 text-sm bg-transparent outline-none" />
            </form>
            {[{ to: "/store", label: "Home", icon: Home }, { to: "/store/catalog", label: "All Products", icon: Package }, { to: "/store/orders", label: "My Orders", icon: Heart }].map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">
                <Icon size={18} />{label}<ChevronRight size={14} className="ml-auto text-gray-400" />
              </Link>
            ))}
            <div className="pt-2 border-t border-gray-100">
              {collections.map(c => (
                <Link key={c.id} to={`/store/catalog?collection=${c.slug}`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 rounded-xl">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 md:py-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="font-semibold" style={{ color: "#0F1E38" }}>{settings.store_name || "CPM Store"}</div>
          <div className="flex items-center gap-6">
            <Link to="/store" className="hover:text-yellow-600">Home</Link>
            <Link to="/store/catalog" className="hover:text-yellow-600">Products</Link>
            <Link to="/store/orders" className="hover:text-yellow-600">Orders</Link>
            <Link to="/app" className="hover:text-yellow-600">← CPM Community</Link>
          </div>
          <div>© {new Date().getFullYear()} CelestialPeopleMeet</div>
        </div>
      </footer>
    </div>
  );
}
